if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        // Letter pendant forms keep the variant ID input disabled until the
        // letter builder validates the typed letters and picks a variant.
        if (!this.form.querySelector('[data-letter-property]')) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('.ds-btn__label') || this.submitButton.querySelector('span');
        this.usesPricedAddToCart = this.dataset.pricedAddToCart === 'true';
        // data-after-add="stay": adds from inside a page that must not navigate
        // (the cart's add-on rails) stay put and re-render in place.
        this.staysOnPage = this.dataset.afterAdd === 'stay';
      }

      connectedCallback() {
        if (this.usesPricedAddToCart) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      onVariantChange({ data: { sectionId, variant } }) {
        if (String(sectionId) !== String(this.dataset.sectionId)) return;
        this.syncAddToCartButton(variant);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Required Ring Size (line item property) must be selected before
        // the product can be added to the cart. The selector shows its own
        // inline error and scrolls/focuses itself when invalid.
        const ringSizeSelector = this.form.querySelector('ring-size-selector');
        if (ringSizeSelector?.reportRingSizeValidity && !ringSizeSelector.reportRingSizeValidity()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        config.body = formData;

        // Stamp the private fulfillment property from fresh inventory (see
        // icebox-fulfillment-type.js). Resolves to the Liquid default already
        // in the form if the lookup fails, so the add is never blocked.
        this.prepareFulfillmentType(formData)
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
          .then(({ ok, data: response }) => {
            if (!ok || response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(
                response.description || response.message || window.cartStrings?.error
              );

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Cart-page model: publish the update (header count, the cart page's
            // own re-render), then honour after_add.
            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            this.error = false;
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });
            if (window.IceboxAfterAdd) window.IceboxAfterAdd.handle({ navigateAllowed: !this.staysOnPage });
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.handleErrorMessage(window.cartStrings?.error || 'Something went wrong. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      prepareFulfillmentType(formData) {
        const helper = window.IceboxFulfillment;
        if (!helper) return Promise.resolve();

        const key = `properties[${helper.PROPERTY}]`;
        const fallback = formData.get(key) || helper.MADE_TO_ORDER;
        const productInfo = this.closest('product-info');
        const productUrl = productInfo?.dataset.url || this.form.dataset.productUrl || window.location.pathname;

        return helper
          .prepareAdd({
            variantId: formData.get('id'),
            quantity: formData.get('quantity') || 1,
            productUrl,
            fallback,
          })
          .then((value) => {
            formData.set(key, value);
            // Keep the rendered hidden input in step so a native (non-JS)
            // submit or a later read sees the same estimate.
            const input = this.form.querySelector(`input[name="${key}"]`);
            if (input) input.value = value;
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.usesPricedAddToCart) {
            this.updateAddToCartLabel();
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      syncAddToCartButton(variant) {
        if (!this.usesPricedAddToCart) return;

        if (!variant) {
          this.toggleSubmitButton(true, window.variantStrings.unavailable);
          return;
        }

        if (!variant.available) {
          this.toggleSubmitButton(true, window.variantStrings.soldOut);
          return;
        }

        this.toggleSubmitButton(false);
      }

      updateAddToCartLabel() {
        const priceText = this.getDisplayedPrice();
        this.submitButtonText.textContent = priceText
          ? `${window.variantStrings.addToCartWithPricePrefix} ${priceText}`
          : window.variantStrings.addToCartWithPriceFallback;
      }

      getDisplayedPrice() {
        const priceRoot = document.querySelector(`#price-${this.dataset.sectionId}`);
        if (!priceRoot) return '';

        if (priceRoot.classList.contains('price--on-sale')) {
          return priceRoot.querySelector('.price-item--sale')?.textContent.trim() || '';
        }

        return priceRoot.querySelector('.price__regular .price-item--regular')?.textContent.trim() || '';
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
